import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootReducerState } from '../../redux/Reducers';
import axios from 'axios';
import { rejectWithApiError } from '../../utils/api';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import {
  CreateStarlinkCircuitDto,
  StarlinkCircuitListItemType,
  StarlinkCircuitType,
  UpdateStarlinkCircuitDto,
} from '../../types/starlink/starlink.types';

export const postStarlinkCircuitDetails = createAsyncThunk<
  StarlinkCircuitType,
  { data: CreateStarlinkCircuitDto },
  { state: RootReducerState }
>('starlink/postStarlinkCircuitDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<StarlinkCircuitType>(`${process.env.REACT_APP_API_URL}/starlink-circuit`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating starlink circuit!' });
  }
});

export const getStarlinkCircuitsList = createAsyncThunk<
  PaginationType<StarlinkCircuitListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('starlink/getStarlinkCircuitsList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<StarlinkCircuitListItemType>>(
      `${process.env.REACT_APP_API_URL}/starlink-circuit${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching starlink circuits!' });
  }
});

export const getStarlinkCircuitDetails = createAsyncThunk<
  StarlinkCircuitType,
  { id: number },
  { state: RootReducerState }
>('starlink/getStarlinkCircuitDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<StarlinkCircuitType>(`${process.env.REACT_APP_API_URL}/starlink-circuit/${id}`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching starlink circuit!' });
  }
});

export const patchStarlinkCircuitDetails = createAsyncThunk<
  StarlinkCircuitType,
  { id: number; data: UpdateStarlinkCircuitDto },
  { state: RootReducerState }
>('starlink/patchStarlinkCircuitDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<StarlinkCircuitType>(
      `${process.env.REACT_APP_API_URL}/starlink-circuit/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating starlink circuit!' });
  }
});

export const deleteStarlinkCircuitDetails = createAsyncThunk<
  StarlinkCircuitType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('starlink/deleteStarlinkCircuitDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<StarlinkCircuitType>(`${process.env.REACT_APP_API_URL}/starlink-circuit/${id}`);
    if (fetchList)
      thunkAPI.dispatch(getStarlinkCircuitsList(thunkAPI.getState().starlinkReducer.starlinkCircuitsRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting starlink circuit!' });
  }
});
